import { cn } from "@/lib/utils/cn";

type Variant = "mark" | "wordmark" | "lockup";
type Tone = "default" | "on-green" | "outline";
type Size = "sm" | "md" | "lg" | number;

type LogoProps = {
  variant?: Variant;
  tone?: Tone;
  size?: Size;
  className?: string;
  title?: string;
};

// Wordmark heights — handoff defaults to 26px; allow sm/md/lg shortcuts.
const sizeMap: Record<Exclude<Size, number>, number> = { sm: 18, md: 26, lg: 40 };

type Resolved = { color: string; accent: string };
function resolveColors(tone: Tone): Resolved {
  switch (tone) {
    case "on-green":
      return { color: "#FFFFFF", accent: "var(--kd-sunny)" };
    case "outline":
      return { color: "var(--kd-navy)", accent: "var(--kd-sunny)" };
    case "default":
    default:
      return { color: "var(--kd-green)", accent: "var(--kd-sunny)" };
  }
}

/**
 * Sianko wordmark — literal "Sianko" in Bricolage 800 with three gold strands above the last "o".
 * Strands drop below 16px; rendered as inline SVG so it scales lossless from favicon to banner.
 */
function Wordmark({
  size,
  tone = "default",
  title,
}: {
  size: number;
  tone?: Tone;
  title?: string;
}) {
  const { color } = resolveColors(tone);
  return (
    <span
      role="img"
      aria-label={title ?? "Sianko"}
      className="inline-flex items-end relative leading-none"
    >
      <span
        className="font-display relative"
        style={{
          fontSize: size,
          fontWeight: 800,
          color,
          letterSpacing: "-0.045em",
          lineHeight: 1,
        }}
      >
        Sianko
      </span>
    </span>
  );
}

export function Logo({
  variant = "mark",
  tone = "default",
  size = "md",
  className,
  title = "Sianko",
}: LogoProps) {
  const px = typeof size === "number" ? size : sizeMap[size];

  // All three variants render the same wordmark — kept for back-compat with call sites
  // (`variant="lockup"` from Login etc. should still work). The handoff defines a single mark.
  return (
    <span className={cn("inline-flex items-center", className)}>
      <Wordmark size={px} tone={tone} title={title} />
    </span>
  );
}

// Re-export Wordmark for callers that want it directly (e.g. AppHeader at exact 26px).
export { Wordmark as SiankoMark };
